// src/redux/slices/chat.js
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios'; // Usando seu caminho

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  contacts: { byId: {}, allIds: [] },
  conversations: { byId: {}, allIds: [] },
  activeConversationId: null,
  participants: [],
  recipients: [],
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // GET CONVERSATIONS (handler para /api/chat/my)
    getConversationsSuccess(state, action) {
      const conversations = action.payload;

      const newConversationsById = {};
      conversations.forEach((conv) => {
        newConversationsById[conv._id] = {
          ...state.conversations.byId[conv._id],
          ...conv,
        };
        if (Array.isArray(newConversationsById[conv._id].messages)) {
          newConversationsById[conv._id].messages = newConversationsById[conv._id].messages.map((message) => ({
            ...message,
            internalCorrelationId: message.internalCorrelationId || message.uid || message._id || null,
          }));
        }
        if (
          state.activeConversationId === conv._id &&
          state.conversations.byId[state.activeConversationId] &&
          state.conversations.byId[state.activeConversationId].messages.length > conv.messages.length
        ) {
          newConversationsById[conv._id].messages = state.conversations.byId[state.activeConversationId].messages;
        }
      });

      state.conversations.byId = newConversationsById;
      state.conversations.allIds = Object.keys(state.conversations.byId);
      state.isLoading = false;
    },
    // GET CONVERSATION (handler para /api/chat/:id)
    getConversationSuccess(state, action) {
      const conversation = { ...action.payload };

      if (conversation) {
        const existing = state.conversations.byId[conversation._id];

        const normalizeEntry = (entry) => {
          if (!entry) {
            return null;
          }
          const cloned = { ...entry };
          if (!cloned.agentId) {
            cloned.agentId = cloned.userId || cloned.authorId || null;
          }
          if (!cloned.agentName) {
            cloned.agentName = cloned.name || cloned.displayName || null;
          }
          if (!cloned.name) {
            cloned.name = cloned.agentName || null;
          }
          return cloned;
        };

        const extractList = (sourceConversation) => {
          if (!sourceConversation) {
            return [];
          }
          if (Array.isArray(sourceConversation.comments) && sourceConversation.comments.length > 0) {
            return sourceConversation.comments.filter((item) => item && item.text).map(normalizeEntry);
          }
          if (sourceConversation.comment && sourceConversation.comment.text) {
            return [normalizeEntry(sourceConversation.comment)];
          }
          return [];
        };

        const existingList = extractList(existing);
        const incomingList = extractList(conversation);
        const combined = [...existingList, ...incomingList];

        const dedupeMap = new Map();
        combined.forEach((item) => {
          if (!item) {
            return;
          }
          const key = item._id || `${item.createdAt || ''}-${item.text || ''}`;
          if (!dedupeMap.has(key)) {
            dedupeMap.set(key, {
              ...item,
              agentId: item.agentId || (conversation.agentDetail && conversation.agentDetail._id) || null,
              agentName: item.agentName || (conversation.agentDetail && conversation.agentDetail.name) || null,
            });
          }
        });

        const mergedComments = Array.from(dedupeMap.values()).sort((a, b) => {
          const dateA = a.updatedAt || a.createdAt || a.timestamp;
          const dateB = b.updatedAt || b.createdAt || b.timestamp;
          const valueA = dateA ? new Date(dateA).getTime() : 0;
          const valueB = dateB ? new Date(dateB).getTime() : 0;
          return valueA - valueB;
        });

        if (mergedComments.length > 0) {
          conversation.comments = mergedComments;
          conversation.comment = { ...mergedComments[mergedComments.length - 1] };
        } else if (conversation.comment) {
          conversation.comment = normalizeEntry(conversation.comment);
        }

        if (Array.isArray(conversation.messages)) {
          conversation.messages = conversation.messages.map((message) => ({
            ...message,
            internalCorrelationId: message.internalCorrelationId || message.uid || message._id || null,
          }));
        }

        state.conversations.byId[conversation._id] = conversation;

        state.participants = [conversation.sender];
        state.activeConversationId = conversation._id;
        if (!state.conversations.allIds.includes(conversation._id)) {
          state.conversations.allIds.push(conversation._id);
        }
      } else {
        state.activeConversationId = null;
      }
      state.isLoading = false;
    },
    // ON SEND MESSAGE
    onSendMessage(state, action) {
      const conversation = action.payload;
      const {
        conversationId,
        uid,
        text,
        from,
        timestamp,
        attachmentType,
        sentByMe,
        attachments = false,
        urlForce = false,
        replyingTo,
        replyingToMetadata,
      } = conversation;

      const finalAttachmentType =
        urlForce && attachments && attachments.length > 0 ? attachments[0].type : attachmentType;

      const newMessage = {
        text,
        uid,
        internalCorrelationId: uid,
        from,
        timestamp,
        attachmentType: finalAttachmentType,
        attachments,
        urlForce,
        sentByMe,
        status: 'sending', // 'sending', 'sent', 'failed'
        replyingTo,
        replyingToMetadata,
      };

      if (!urlForce) {
        delete newMessage.attachments;
        delete newMessage.urlForce;
      }
      if (state.conversations.byId[conversationId] !== undefined) {
        state.conversations.byId[conversationId].messages.push(newMessage);
        // if has other user messagens.
        const existeMensagemRecebida = state.conversations.byId[conversationId].messages.some(
          (x) => x.sentByMe === false
        );
        if (existeMensagemRecebida) state.conversations.byId[conversationId].canReplyComputed = true;
      }
      state.isLoading = false;
    },
    // ON RECIVE MENSSAGE (Corrigido para onReceiveMessage)
    onReceiveMessage(state, action) {
      const messageData = action.payload;
      const {
        conversationId,
        uid,
        text,
        from,
        timestamp,
        attachments,
        replyingTo,
        replyingToMetadata: incomingReplyingToMetadata,
        location,
        contacts,
      } = messageData;

      const conversation = state.conversations.byId[conversationId];

      if (conversation) {
        // Evitar duplicatas se a mensagem já existir (verificação básica por uid)
        if (!conversation.messages.some((msg) => msg.uid === uid)) {
          let replyingToMetadata = incomingReplyingToMetadata;
          if (!replyingToMetadata && replyingTo) {
            const referencedMsg = conversation.messages.find(
              (msg) =>
                msg.uid === replyingTo ||
                msg._id === replyingTo ||
                (msg.internalCorrelationId && msg.internalCorrelationId === replyingTo)
            );
            if (referencedMsg) {
              const {
                text: referencedOriginalText,
                attachments: referencedAttachments,
                location: referencedLocation,
                contacts: referencedContacts,
                sentByMe: referencedSentByMe,
                name: referencedOriginalName,
              } = referencedMsg;

              let referencedText = referencedOriginalText;
              if (!referencedText && referencedAttachments?.length) {
                const firstAttachment = referencedAttachments[0];
                const attachmentType = firstAttachment?.attachmentType || 'arquivo';
                referencedText = `Anexo: ${attachmentType}`;
              }
              if (!referencedText && referencedLocation) {
                referencedText = 'Localização compartilhada';
              }
              if (!referencedText && referencedContacts?.length) {
                referencedText = 'Contato compartilhado';
              }
              if (!referencedText) {
                referencedText = 'Sem prévia disponível';
              }

              const referencedName = referencedSentByMe
                ? referencedOriginalName || 'Você'
                : conversation.sender?.name || conversation.sender?.uid;

              replyingToMetadata = {
                name: referencedName,
                text: referencedText,
                attachmentType:
                  referencedAttachments && referencedAttachments.length > 0
                    ? referencedAttachments[0]?.attachmentType
                    : undefined,
              };
            }
          }

          const newMessage = {
            uid,
            text,
            from,
            timestamp,
            attachments,
            sentByMe: false,
            replyingTo,
            replyingToMetadata,
            location,
            contacts,
          };

          if (state.activeConversationId !== conversationId) {
            conversation.unreadMessageCount = (conversation.unreadMessageCount || 0) + 1;
          }
          conversation.messages.push(newMessage);
          conversation.canReplyComputed = true;

          // Move a conversa para o topo da lista
          state.conversations.allIds = state.conversations.allIds.filter((id) => id !== conversationId);
          state.conversations.allIds.unshift(conversationId);
        }
      }
      state.isLoading = false;
    },
    // ON NEED UPDATE MESSAGE
    updateMessageStatus(state, action) {
      const { conversationId, uid, status } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        const message = conversation.messages.find((msg) => msg.uid === uid);
        if (message) {
          message.status = status;
        }
      }
    },
    updateMessageAttachments(state, action) {
      const { conversationId, uid, attachments } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        const message = conversation.messages.find((msg) => msg.uid === uid);
        if (message) {
          message.attachments = attachments;
        }
      }
    },
    updateMessageUid(state, action) {
      const { conversationId, internalCorrelationId, uid } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation && Array.isArray(conversation.messages)) {
        const message = conversation.messages.find(
          (msg) =>
            msg.internalCorrelationId === internalCorrelationId || msg.uid === internalCorrelationId || msg.uid === uid
        );
        if (message) {
          message.uid = uid || message.uid;
          message.internalCorrelationId = uid || message.internalCorrelationId;
        }
      }
    },
    // Atualiza o texto da mensagem (usado para templates com variáveis processadas pelo backend)
    updateMessageText(state, action) {
      const { conversationId, uid, text } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        const message = conversation.messages.find((msg) => msg.uid === uid || msg.internalCorrelationId === uid);
        if (message && text) {
          message.text = text;
        }
      }
    },
    markConversationAsReadSuccess(state, action) {
      const { conversationId } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        conversation.unreadMessageCount = 0;
      }
      state.isLoading = false;
    },
    // GET PARTICIPANTS
    getParticipantsSuccess(state, action) {
      const participants = action.payload;
      state.participants = participants;
    },
    // RESET ACTIVE CONVERSATION
    resetActiveConversation(state) {
      state.activeConversationId = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
// Corrigido o nome da action onReceiveMessage
export const {
  resetActiveConversation,
  onReceiveMessage, // Nome corrigido
  getConversationSuccess,

  markConversationAsReadSuccess, // Adicionado se for usado externamente
  getConversationsSuccess, // Adicionado se for usado externamente
  updateMessageStatus,
  updateMessageAttachments,
  updateMessageUid,
} = slice.actions;

export function getConversations(user) {
  return async (dispatch) => {
    // Removido o dispatch do parâmetro, use o importado
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/chat/my', {
        companyId: user.companyId,
      });
      dispatch(slice.actions.getConversationsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      console.error('Erro em getConversations:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function getConversation(conversationKey, historyPage = 1) {
  return async (dispatch) => {
    // Removido o dispatch do parâmetro
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/chat/${conversationKey}`, {
        params: { historyPage },
      });
      dispatch(slice.actions.getConversationSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      console.error('Erro em getConversation:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function markConversationAsRead(conversationId) {
  return async (dispatch) => {
    // Removido o dispatch do parâmetro
    dispatch(slice.actions.startLoading());
    try {
      // Não precisamos necessariamente esperar a resposta da API para atualizar a UI otimisticamente
      // Mas para consistência, esperamos.
      await axios.post(`/api/chat/${conversationId}/mark-as-read`);
      dispatch(slice.actions.markConversationAsReadSuccess({ conversationId }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      console.error('Erro em markConversationAsRead:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function saveMessage(message) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    // Create a mutable copy of the message object
    const messageToDispatch = { ...message };
    if (messageToDispatch.attachments && messageToDispatch.attachments.length > 0) {
      const attachmentFile = messageToDispatch.attachments[0];
      if (attachmentFile instanceof File) {
        messageToDispatch.attachmentType = attachmentFile.type;
        messageToDispatch.urlForce = true;
      }
    }

    // Show in screen in real time with 'sending' status
    dispatch(slice.actions.onSendMessage(messageToDispatch));

    try {
      const appendReplyingTo = (target) => {
        if (messageToDispatch.replyingTo) {
          if (target instanceof FormData) {
            target.append('replyingTo', messageToDispatch.replyingTo);
          } else {
            target.replyingTo = messageToDispatch.replyingTo;
          }
        }
      };

      let data = {
        internalCorrelationId: messageToDispatch.uid,
        text: messageToDispatch.text,
        sentByMe: messageToDispatch.sentByMe,
      };
      appendReplyingTo(data);

      // Messages type docs and videos
      if (
        messageToDispatch.attachmentType !== 'text' &&
        messageToDispatch.attachmentType !== 'audio' &&
        messageToDispatch.attachmentType !== 'template'
      ) {
        const fdata = new FormData();
        fdata.append('internalCorrelationId', messageToDispatch.uid);
        if (messageToDispatch.text) fdata.append('text', messageToDispatch.text);
        appendReplyingTo(fdata);
        fdata.append('attachment', messageToDispatch.attachments[0]);
        data = fdata;
        // message.urlForce is already set above
      }

      // Messages type video
      if (messageToDispatch.attachmentType === 'video') {
        const fvideo = new FormData();
        fvideo.append('internalCorrelationId', messageToDispatch.uid);
        fvideo.append('text', messageToDispatch.text || '[Video]');
        appendReplyingTo(fvideo);
        fvideo.append('attachment', messageToDispatch.attachments[0]);
        data = fvideo;
      }

      // Messages type audio
      if (messageToDispatch.attachmentType && messageToDispatch.attachmentType.includes('audio')) {
        const fdats = new FormData();
        fdats.append('internalCorrelationId', messageToDispatch.uid);
        fdats.append('text', messageToDispatch.text || '[Audio]');
        fdats.append('attachmentDuration', messageToDispatch.attachmentDuration);
        appendReplyingTo(fdats);
        fdats.append('attachment', messageToDispatch.attachments[0]);
        data = fdats;
        // message.urlForce is already set above
      }
      // Messages type template
      if (messageToDispatch.attachmentType === 'template') {
        const nTemplate = { ...messageToDispatch.template };
        const newParam = [];

        nTemplate.components.forEach((element) => {
          const newParamArray = {
            type: element.type,
            parameters: [],
          };
          if (element.type === 'HEADER') {
            if (element?.format === 'IMAGE') {
              newParamArray.parameters.push({
                type: 'image',
                image: {
                  link: element.example.header_handle[0],
                },
              });
            }
            if (element?.format === 'VIDEO') {
              newParamArray.parameters.push({
                type: 'video',
                video: {
                  link: element.example.header_handle[0],
                },
              });
            }
          }
          if (element.type === 'BODY') {
            // Check if is array
            if (element.example && Array.isArray(element.example?.body_text)) {
              element.example.body_text[0].forEach((body) => {
                newParamArray.parameters.push({
                  type: 'text',
                  text: body,
                });
              });
            }
          }
          if (element.type === 'BUTTONS') {
            const hasDynamicButtons = element.buttons.some(
              (button) =>
                (button.type === 'URL' || button.type === 'PHONE_NUMBER') &&
                Array.isArray(button.parameters) &&
                button.parameters.length > 0
            );

            if (hasDynamicButtons) {
              element.buttons.forEach((button, i) => {
                const isQuickReply = button.type === 'QUICK_REPLY';
                const isDynamic =
                  (button.type === 'URL' || button.type === 'PHONE_NUMBER') &&
                  Array.isArray(button.parameters) &&
                  button.parameters.length > 0;

                if (isDynamic || isQuickReply) {
                  newParam.push({
                    type: 'button',
                    sub_type: button.type,
                    index: `${i}`,
                    parameters: isQuickReply ? [{ type: 'text', text: button.text }] : button.parameters,
                  });
                }
              });
            }

            return;
          }
          if (newParamArray.parameters.length > 0) {
            newParam.push(newParamArray);
          }
        });

        nTemplate.components = newParam;
        data.template = nTemplate;
      }

      if (messageToDispatch.requestLocation) {
        data.requestLocation = true;
      }

      const response = await axios.post(`/api/chat/${messageToDispatch.conversationId}/send`, data);
      dispatch(
        slice.actions.updateMessageStatus({
          conversationId: messageToDispatch.conversationId,
          uid: messageToDispatch.uid,
          status: 'sent',
        })
      );

      if (response.data?.text) {
        dispatch(
          slice.actions.updateMessageText({
            conversationId: messageToDispatch.conversationId,
            uid: messageToDispatch.uid,
            text: response.data.text,
          })
        );
      }

      // If the server returns updated attachment info (e.g., permanent URL), update the message
      if (response.data?.attachments && response.data.attachments.length > 0) {
        dispatch(
          slice.actions.updateMessageAttachments({
            conversationId: messageToDispatch.conversationId,
            uid: messageToDispatch.uid,
            attachments: response.data.attachments,
          })
        );
      }
    } catch (error) {
      dispatch(
        slice.actions.updateMessageStatus({
          conversationId: messageToDispatch.conversationId,
          uid: messageToDispatch.uid,
          status: 'failed',
        })
      );
      dispatch(slice.actions.hasError(error));
    }
  };
}

export const handleRemoteChatFinalization = (finalizedChatData, currentUser) => async (dispatch, getState) => {
  const { chatId: finalizedChatId, agentId: agentIdOfFinalizedChat } = finalizedChatData;

  if (currentUser && currentUser._id === agentIdOfFinalizedChat) {
    const { activeConversationId } = getState().chat;

    if (finalizedChatId === activeConversationId) {
      dispatch(resetActiveConversation());
    }

    dispatch(getConversations(currentUser));
  }
};
